import { CheckCircle2, Link2Off, RefreshCw, ShieldAlert } from 'lucide-react'
import { toast } from 'sonner'

import type { Channel } from '@/api/endpoints/channels'
import { startOAuthOrReport } from '@/api/endpoints/oauth'
import { useDisconnectYoutube, useUploadStatus } from '@/api/endpoints/youtube/uploads'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { errorMessage } from '@/lib/errors'
import { PlatformCard, PlatformDetail } from '../../PlatformCard'
import styles from '../../PlatformCard.module.scss'

interface YoutubeAccountCardProps {
  channel: Channel
}

/**
 * YouTube, a plataforma principal do canal: decide o que sai e quando. É a
 * antiga seção "Conexão YouTube", agora como um cartão da seção Plataformas.
 *
 * O status consulta ``GET /api/v1/uploads/status`` a cada 15 s e troca o chip
 * entre "Conectado", "Desconectado" e "Sem client_secret" sozinho.
 */
export function YoutubeAccountCard({ channel }: YoutubeAccountCardProps) {
  const status = useUploadStatus(channel.slug)
  const disconnect = useDisconnectYoutube()

  const auth = status.data?.auth
  const connected = auth?.is_authenticated === true
  const configured = auth?.is_configured === true

  const chip = status.isPending ? (
    <Chip>verificando...</Chip>
  ) : status.isError ? (
    <Chip variant="danger" leadingIcon={<ShieldAlert />}>
      erro
    </Chip>
  ) : !configured ? (
    <Chip variant="warning">Sem client_secret</Chip>
  ) : connected ? (
    <Chip variant="success" leadingIcon={<CheckCircle2 />}>
      Conectado
    </Chip>
  ) : (
    <Chip variant="warning">Desconectado</Chip>
  )

  return (
    <PlatformCard
      platform="youtube"
      kind="principal"
      status={chip}
      details={
        channel.channel_tag ? <PlatformDetail label="@ do canal">{channel.channel_tag}</PlatformDetail> : undefined
      }
      actions={
        <>
          <Button
            size="sm"
            variant={connected ? 'secondary' : 'primary'}
            leadingIcon={<RefreshCw />}
            onClick={() => {
              startOAuthOrReport('youtube', channel.slug, (m) => toast.error(m))
            }}
            disabled={!configured}
          >
            {connected ? 'Reconectar' : 'Conectar'}
          </Button>
          {connected ? (
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Link2Off />}
              disabled={disconnect.isPending}
              onClick={() => {
                disconnect.mutate(channel.slug, {
                  onSuccess: () => {
                    toast.success(`${channel.name} desconectado`)
                  },
                  onError: (err) => {
                    toast.error(errorMessage(err, 'Falha ao desconectar.'))
                  },
                })
              }}
            >
              Desconectar
            </Button>
          ) : null}
        </>
      }
    >
      <p className={styles.note}>OAuth pra esse canal. Reconecte quando os escopos mudarem.</p>
      {!configured && !status.isPending ? (
        <p className={styles.note}>
          Adicione o <code>client_secret.json</code> em <code>data/channels/{channel.slug}/</code> e reinicie o
          servidor pra habilitar OAuth.
        </p>
      ) : null}
    </PlatformCard>
  )
}
